// src/components/ProductFilters.tsx
//
// The browse page's left filter rail. Collapsible facet groups over the values
// that actually exist in the category being viewed — there is no facet taxonomy
// anywhere in Square, so the options are derived from the items on the page and
// a category with a single colour simply doesn't get a Colour group.

import React, { useEffect, useRef, useState } from "react";
import StyledCheckbox from "./StyledCheckbox";
import { ProductFilterSelection } from "../extensions/helpers";

export type FilterOption = {
  id: string;
  name: string;
  /** Hex without the leading "#", as Square stores it on a colour option value. */
  color?: string;
};

export type FilterFacet = {
  /** Which array in ProductFilterSelection this group writes to. */
  key: keyof ProductFilterSelection;
  label: string;
  options: FilterOption[];
};

type ProductFiltersProps = {
  facets: FilterFacet[];
  selection: ProductFilterSelection;
  onToggle: (key: keyof ProductFilterSelection, optionId: string) => void;
  onClear: () => void;
  onClose: () => void;
  selectedCount: number;
};

const ProductFilters = ({
  facets,
  selection,
  onToggle,
  onClear,
  onClose,
  selectedCount,
}: ProductFiltersProps) => {
  // Every group starts open — with two or three short facets there is nothing
  // to hide, and a panel that opens fully collapsed reads as empty.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(facets.map((f) => [f.key, true])),
  );

  const closeRef = useRef<HTMLButtonElement>(null);

  // Below lg the rail is an overlay drawer, so it needs the modal courtesies:
  // focus lands on Close, Escape dismisses. Harmless on desktop, where the
  // panel is in flow and Escape simply collapses it.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      {/* Scrim for the mobile drawer only — on desktop the rail is part of the
          page and dimming the products beside it would be nonsense. */}
      <div
        className="product-filters__scrim"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="product-filters" aria-label="Filter products">
        <div className="product-filters__top">
          <button
            ref={closeRef}
            type="button"
            className="product-filters__close"
            onClick={onClose}
          >
            <span aria-hidden="true">✕</span> Close
          </button>
          {selectedCount > 0 && (
            <button
              type="button"
              className="product-filters__clear"
              onClick={onClear}
            >
              Clear all
            </button>
          )}
        </div>

        {facets.map((facet) => {
          const isOpen = openGroups[facet.key] ?? true;
          const selected = selection[facet.key];
          return (
            <section key={facet.key} className="filter-group">
              <h2>
                <button
                  type="button"
                  className="filter-group__header"
                  aria-expanded={isOpen}
                  aria-controls={`filter-group-${facet.key}`}
                  onClick={() =>
                    setOpenGroups((g) => ({ ...g, [facet.key]: !isOpen }))
                  }
                >
                  <span>{facet.label}</span>
                  <span className="filter-group__sign" aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
              </h2>
              {isOpen && (
                <div
                  className="filter-group__options"
                  id={`filter-group-${facet.key}`}
                >
                  {facet.options.map((option) => (
                    <StyledCheckbox
                      key={option.id}
                      id={`filter-${facet.key}-${option.id}`}
                      checked={selected.includes(option.id)}
                      onChange={() => onToggle(facet.key, option.id)}
                      label={
                        option.color ? (
                          <span className="filter-swatch-label">
                            <span
                              aria-hidden="true"
                              className="filter-swatch"
                              style={{ backgroundColor: `#${option.color}` }}
                            />
                            {option.name}
                          </span>
                        ) : (
                          option.name
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </aside>
    </>
  );
};

export default ProductFilters;
